import { useEffect, useState } from 'react';
import { format, startOfWeek } from 'date-fns';
import { ClipboardCheck, Star } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

export const CATEGORIES = [
  { key: 'fuel_efficiency', label: 'Fuel Efficiency' },
  { key: 'incident_count', label: 'Incident Count' },
  { key: 'inspection_pass', label: 'Inspection Pass Rate' },
  { key: 'cost_per_km', label: 'Cost per KM' },
  { key: 'cleanliness_inside', label: 'Cleanliness — Inside' },
  { key: 'cleanliness_outside', label: 'Cleanliness — Outside' },
];

export const StarRating = ({ value, onChange, readonly = false }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((s) => (
      <button key={s} type="button" disabled={readonly} onClick={() => onChange?.(s)}
        className={`transition-colors ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`} data-testid={`star-${s}`}>
        <Star className={`w-5 h-5 ${s <= value ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
      </button>
    ))}
  </div>
);

export const OverallStars = ({ value, size = 'w-4 h-4' }) => {
  const rounded = Math.round(value);
  const color = value >= 4 ? 'text-green-600' : value >= 3 ? 'text-amber-500' : 'text-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star key={s} className={`${size} ${s <= rounded ? `fill-current ${color}` : 'text-gray-300'}`} />
        ))}
      </div>
      <span className={`text-sm font-bold ${color}`}>{Number(value).toFixed(1)}</span>
    </div>
  );
};

export const overallScore = (sc) => {
  const vals = CATEGORIES.map(({ key }) => sc[key]);
  return vals.reduce((a, b) => a + b, 0) / vals.length;
};

const blankForm = (vehicleId, driverId) => ({
  vehicle_id: vehicleId || '',
  driver_id: driverId || '',
  week_start: format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
  fuel_efficiency: 3, incident_count: 5, inspection_pass: 3,
  cost_per_km: 3, cleanliness_inside: 3, cleanliness_outside: 3, notes: '',
});

const PickerRow = ({ form, setForm, vehicles, drivers, lockVehicle }) => (
  <div className="grid grid-cols-2 gap-4">
    <div className="space-y-2">
      <Label>Driver *</Label>
      <Select value={form.driver_id || undefined} onValueChange={(v) => {
        if (!v) return; // Radix fires '' when options load after value is set
        const assigned = vehicles.find((x) => x.assigned_driver_id === v);
        setForm((f) => ({ ...f, driver_id: v, vehicle_id: lockVehicle || assigned?.id || f.vehicle_id || '' }));
      }}>
        <SelectTrigger data-testid="scorecard-driver-select"><SelectValue placeholder="Select driver" /></SelectTrigger>
        <SelectContent>
          {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-2">
      <Label>Vehicle *</Label>
      <Select value={form.vehicle_id || undefined} disabled={!!lockVehicle} onValueChange={(v) => {
        if (!v) return;
        const veh = vehicles.find((x) => x.id === v);
        setForm((f) => ({ ...f, vehicle_id: v, driver_id: f.driver_id || veh?.assigned_driver_id || '' }));
      }}>
        <SelectTrigger data-testid="scorecard-vehicle-select"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
        <SelectContent>
          {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} ({v.registration})</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  </div>
);

// Weekly driver scorecard form. Pass vehicleId to lock the vehicle (Vehicle page) or driverId to preselect a driver.
export const ScorecardFormDialog = ({ open, onOpenChange, vehicleId, driverId, onSaved }) => {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState(blankForm(vehicleId, driverId));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([api.getVehicles(), api.getDrivers()]).then(([vs, ds]) => {
      setVehicles(vs);
      setDrivers(ds.filter((d) => d.status !== 'inactive'));
      const lockedVeh = vs.find((v) => v.id === vehicleId);
      const drvVeh = driverId ? vs.find((v) => v.assigned_driver_id === driverId) : null;
      setForm({
        ...blankForm(vehicleId || drvVeh?.id, driverId || lockedVeh?.assigned_driver_id),
      });
    }).catch(() => {});
  }, [open, vehicleId, driverId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.driver_id) { toast.error('Please select a driver'); return; }
    if (!form.vehicle_id) { toast.error('Please select a vehicle'); return; }
    setSaving(true);
    try {
      await api.createScorecard(form);
      toast.success('Scorecard saved');
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save scorecard');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="scorecard-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-blue-600" /> Weekly Driver Scorecard
          </DialogTitle>
          <DialogDescription>Rate the driver's performance for the week (1-5 stars per category).</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <PickerRow form={form} setForm={setForm} vehicles={vehicles} drivers={drivers} lockVehicle={vehicleId} />
          <div className="space-y-2">
            <Label>Week Starting</Label>
            <Input type="date" value={form.week_start} className="font-data" data-testid="scorecard-week-input"
              onChange={(e) => setForm((f) => ({ ...f, week_start: e.target.value }))} />
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
            {CATEGORIES.map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between" data-testid={`scorecard-category-${key}`}>
                <Label className="text-sm text-slate-700 min-w-[160px]">{label}</Label>
                <StarRating value={form[key]} onChange={(v) => setForm((f) => ({ ...f, [key]: v }))} />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
            <span className="text-sm font-medium text-blue-900">Overall Score</span>
            <OverallStars value={overallScore(form)} />
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Input value={form.notes} placeholder="Optional comments..." data-testid="scorecard-notes-input"
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-gray-800 hover:bg-gray-700" disabled={saving} data-testid="save-scorecard-btn">
              {saving ? 'Saving...' : 'Save Scorecard'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
